import { supabase } from "./supabase";

// Lightweight project shape used by every surface that needs to pick or
// display a project: the registry sections in ProjectDetail (attendees +
// glossary), the picker on MeetingNew, and the per-row reassign on
// MeetingsList. Soft-deleted projects are filtered out at query time.

export interface ProjectOption {
  id: string;
  name: string;
}

export async function loadActiveProjects(
  workspaceId: string,
): Promise<ProjectOption[]> {
  const { data, error } = await supabase
    .from("projects")
    .select("id, name")
    .eq("workspace_id", workspaceId)
    .is("deleted_at", null)
    .order("name");
  if (error) throw error;
  return (data ?? []) as ProjectOption[];
}

// Sticky "last project used" pointer, scoped per workspace so a future
// workspace switcher doesn't bleed selections across tenants. Stored
// in localStorage; cleared on sign-out is not necessary (it's
// workspace-scoped, not user-scoped, and the next user with access to
// the workspace would benefit from the same hint).
//
// Values: a project id, or the literal "__unassigned__" sentinel.
// null = never picked anything; caller falls back to "Unassigned".
const LAST_PROJECT_KEY_PREFIX = "timlul.lastProjectId.";
export const UNASSIGNED_SENTINEL = "__unassigned__";

export function getLastProjectChoice(workspaceId: string): string | null {
  try {
    return localStorage.getItem(LAST_PROJECT_KEY_PREFIX + workspaceId);
  } catch {
    return null;
  }
}

export function setLastProjectChoice(
  workspaceId: string,
  value: string | null,
): void {
  try {
    if (value === null) {
      localStorage.removeItem(LAST_PROJECT_KEY_PREFIX + workspaceId);
    } else {
      localStorage.setItem(LAST_PROJECT_KEY_PREFIX + workspaceId, value);
    }
  } catch {
    /* best-effort */
  }
}
